import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Sparkles, Download, Eye, Users, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOUNDER_EMAIL = 'demo@cartieai.com';

/**
 * LeadMagnetConversionCard
 * --------------------------
 * Founder-only roll-up for the /admin/analytics page. Shows for each
 * cornerstone-attached lead magnet:
 *   - subscribers (emails captured in the window)
 *   - downloads (PDF generations)
 *   - post views (analytics events on the parent post)
 *   - conversion rate (subscribers ÷ post_views)
 *
 * Best-converting magnet sits at the top so the founder can spot which
 * cornerstone topic to invest in next at a glance.
 */
export const LeadMagnetConversionCard = ({ days = 30 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/lead-magnets/admin/conversion?days=${days}`, {
        headers: { 'X-Founder-Email': FOUNDER_EMAIL },
      })
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return (
      <Card className="bg-slate-900 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950/20 border border-emerald-500/20">
        <CardContent className="p-6 flex items-center gap-3 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin text-emerald-300" />
          Loading lead-magnet conversion…
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.magnets) {
    return null;
  }

  const totals = data.totals || {};
  return (
    <Card
      data-testid="lead-magnet-conversion-card"
      className="bg-slate-900 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950/30 border border-emerald-500/25 shadow-[0_0_60px_-25px_rgba(16,185,129,0.4)]"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/30 to-cyan-500/15 border border-emerald-400/40 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-emerald-300" />
            </div>
            <div>
              <CardTitle className="text-white text-base flex items-center gap-2">
                Lead-Magnet Conversion
                <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-400/30 text-[11px] tracking-widest">
                  LAST {data.window_days}D
                </Badge>
              </CardTitle>
              <p className="text-[11px] text-slate-400 mt-1">
                Which cornerstone is converting best — invest in similar topics.
              </p>
            </div>
          </div>
          <div
            className="text-right flex items-baseline gap-3 text-[11px] text-slate-400"
            data-testid="lead-magnet-totals"
          >
            <div>
              <span className="font-bold text-emerald-300 text-base tabular-nums">
                {totals.subscribers || 0}
              </span>{' '}
              subs
            </div>
            <div>
              <span className="font-bold text-cyan-300 text-base tabular-nums">
                {totals.downloads || 0}
              </span>{' '}
              dl
            </div>
            <div>
              <span className="font-bold text-cyan-300 text-base tabular-nums">
                {totals.post_views || 0}
              </span>{' '}
              views
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {data.magnets.length === 0 ? (
          <p className="text-sm text-slate-400 italic">
            No lead magnets captured yet.
          </p>
        ) : (
          <div className="space-y-2">
            {data.magnets.map((m) => {
              const rate = m.conversion_rate_pct;
              const rateLabel =
                rate == null ? '—' : `${rate.toFixed(1)}%`;
              const isLeader = data.magnets[0].slug === m.slug && (m.subscribers > 0);
              return (
                <div
                  key={m.slug}
                  data-testid={`lead-magnet-row-${m.slug}`}
                  className={`rounded-lg border p-3 flex items-center gap-4 flex-wrap transition-colors ${
                    isLeader
                      ? 'bg-emerald-500/10 border-emerald-400/30'
                      : 'bg-slate-900 border-slate-800'
                  }`}
                >
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-white text-sm">
                        {m.title}
                      </span>
                      {isLeader && (
                        <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/40 text-[11px] tracking-wider">
                          LEADER
                        </Badge>
                      )}
                    </div>
                    {m.post_slug && (
                      <a
                        href={`/blog/${m.post_slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-slate-400 hover:text-slate-300 inline-flex items-center gap-1 mt-0.5"
                      >
                        /blog/{m.post_slug}
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-[11px] tabular-nums">
                    <div
                      className="text-slate-300 flex items-center gap-1"
                      title="Subscribers"
                    >
                      <Users className="w-3 h-3 text-emerald-300" />
                      {m.subscribers}
                    </div>
                    <div
                      className="text-slate-300 flex items-center gap-1"
                      title="Downloads"
                    >
                      <Download className="w-3 h-3 text-cyan-300" />
                      {m.downloads}
                    </div>
                    <div
                      className="text-slate-300 flex items-center gap-1"
                      title="Post views"
                    >
                      <Eye className="w-3 h-3 text-cyan-300" />
                      {m.post_views}
                    </div>
                    <div
                      className={`px-2 py-0.5 rounded-md font-bold text-[11px] border ${
                        rate == null
                          ? 'text-slate-400 border-slate-700 bg-slate-800'
                          : rate >= 5
                          ? 'text-emerald-200 border-emerald-400/40 bg-emerald-500/15'
                          : rate >= 2
                          ? 'text-amber-200 border-amber-400/40 bg-amber-500/15'
                          : 'text-rose-200 border-rose-400/40 bg-rose-500/15'
                      }`}
                      data-testid={`lead-magnet-rate-${m.slug}`}
                    >
                      {rateLabel}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-3 italic">
          Conversion = subscribers ÷ post views. Green &gt;5%, amber 2-5%, rose &lt;2%, dash = no views yet.
        </p>
      </CardContent>
    </Card>
  );
};

export default LeadMagnetConversionCard;
